<template>
  <content-box class="change-plan--wrap" :width="'530px'">
    <p class="title">
      <template v-if="isNew">
        {{ $t('common.planSubscription') }}
      </template>
      <template v-else>
        {{ $t('userInfo.managePlan.changePlan.title') }}
      </template>
    </p>
    <div class="header">
      <p v-if="isNew && !planData.isTrial" class="current-plan">
        <span class="label">{{ $t('userInfo.managePlan.changePlan.label.subscribeTo') }}</span>
        <strong class="plan-name">{{ newPlanName }}</strong>
      </p>
      <template v-else>
        <p class="current-plan">
          <span class="label">{{ $t('userInfo.managePlan.changePlan.label.nowUsing') }}</span>
          <strong class="plan-name">{{ planData.planName }}</strong>
        </p>
        <i class="icon icon--arrow-gray-size40"></i>
        <p class="next-plan">
          <span class="label">{{ $t('userInfo.managePlan.changePlan.label.changeTo') }}</span>
          <strong class="plan-name">{{ newPlanName }}</strong>
        </p>
      </template>
    </div>
    <div class="plan-info--wrap">
      <div class="next-plan">
        <p class="label">{{ newPlanName }} {{ $t('common.price') }}</p>
        <div class="value">
          <p>₩ {{ newPlanPrice | thousand }}</p>
          <span v-if="!this.isUpgrade || useCoupon">({{ $t('common.vat') }})</span>
        </div>
      </div>
      <template v-if="isUpgrade && !useCoupon">
        <div class="current-plan">
          <p class="label">{{ planData.planName }} {{ $t('userInfo.managePlan.changePlan.label.remainingPvDiscount') }}</p>
          <div class="value">
            <p>( - ) ₩ {{ userData.remainPv | thousand }}</p>
          </div>
        </div>
        <div class="total-price">
          <p class="label">{{ $t('userInfo.managePlan.changePlan.label.totalAmount') }}</p>
          <div class="value">
            <p>₩ {{ totalPrice | thousand }}</p>
            <span>({{ $t('common.vat') }})</span>
            <span>({{ $t('common.cutOffDecimalPoint') }})</span>
          </div>
        </div>
      </template>
    </div>
    <div class="notice--wrap">
      <ul>
        <template v-if="isNew">
          <li>
            <p>
              {{ $t('userInfo.managePlan.changePlan.notice.isNew') }}
            </p>
          </li>
        </template>
        <template v-else-if="isUpgrade">
          <li>
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isUpgrade.desc1')"></p>
          </li>
          <li v-if="!useCoupon">
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isUpgrade.desc2', {planName: planData.planName, newPlanName: newPlanName})"></p>
          </li>
        </template>
        <template v-else-if="isDowngrade">
          <li>
            <p v-html="$t('userInfo.managePlan.changePlan.notice.isDowngrade.desc', {newPlanName: newPlanName, expireDate: expireDateFormat})"></p>
          </li>
        </template>
        <li v-if="downgradeLimitations.length">
          <p>{{ $t('userInfo.managePlan.changePlan.notice.isDowngrade.limitations.desc', {newPlanName: newPlanName}) }}</p>
          <ol>
            <li v-for="(item, index) in downgradeLimitations" :key="index">
              {{ index + 1 }}. {{ $t(`userInfo.managePlan.changePlan.notice.isDowngrade.limitations.${item.text}`) }}
              <button v-if="item.more" @click="showLimitation(item.more)">{{ $t('common.seeMore') }} ></button>
            </li>
          </ol>
        </li>
      </ul>
    </div>
    <div class="footer">
      <ui-checkbox v-model="isTermsAgree">
        <span class="terms-agree">
          {{ $t('userInfo.managePlan.changePlan.footer.checkbox') }}
        </span>
      </ui-checkbox>
      <span v-if="hasTermsError" class="terms-agree-validation-text">{{ $t('common.validationErrorText.requiredAgree') }}</span>
      <div class="button--wrap">
        <ui-button class="btn__cancel"
                   :primary2="true"
                   @click="onCloseLayer">{{ $t('common.cancel') }}</ui-button>
        <ui-button class="btn__confirm"
                   :primary="true"
                   @click="onConfirm">{{ $t('common.confirm') }}</ui-button>
      </div>
    </div>
  </content-box>
</template>

<script>
import { addDays } from 'date-fns'
import { getLocaleDateStr } from '@/libs/utils'
import { PLAN_NUMBER } from '@/libs/plan-helper'
import ContentBox from '@/components/ui/ContentBox'
import UiButton from '@/components/ui/UiButton'
import UiCheckbox from '@/components/ui/UiCheckbox'

export default {
  components: {
    ContentBox,
    UiButton,
    UiCheckbox
  },
  props: [
    'userData',
    'planData',
    'newPlanData'
  ],
  data () {
    return {
      hasTermsError: false,
      isTermsAgree: false
    }
  },
  computed: {
    useCoupon () {
      return this.planData.useCoupon
    },
    isNew () {
      return this.planData.canCreate
    },
    isUpgrade () {
      return !this.isNew && this.planData.planNumber < this.newPlanData.planNumber
    },
    isDowngrade () {
      return !this.isNew && !this.isUpgrade
    },
    newPlanName () {
      return this.newPlanData.name
    },
    newPlanNumber () {
      return this.newPlanData.planNumber
    },
    newPlanPrice () {
      const ratio = (this.isUpgrade && !this.planData.useCoupon) ? 1 : 1.1
      return this.newPlanData.price * ratio
    },
    totalPrice () {
      return Math.floor((this.newPlanData.price - this.userData.remainPv) * 1.1)
    },
    downgradeLimitations () {
      const items = []
      const hasProFeature = this.planData.servicePlanNumber >= PLAN_NUMBER.pro
      const hasStandardFeature = this.planData.servicePlanNumber >= PLAN_NUMBER.standard

      if (hasProFeature && this.newPlanNumber < PLAN_NUMBER.pro) {
        items.push({
          text: 'funnel',
          more: 'funnel'
        })
      }

      if (hasStandardFeature && this.newPlanNumber < PLAN_NUMBER.standard) {
        items.push({
          text: 'bLogin',
          more: 'bLogin'
        })
        items.push({
          text: 'weeklyReport'
        })
      }
      return items
    },
    expireDateFormat () {
      return getLocaleDateStr(addDays(this.planData.endDate, 1))
    }
  },
  created () {
  },
  methods: {
    showLimitation (more) {
      this.$emit('update:downgradeMore', more)
      // this.onCloseLayer()
    },
    onCloseLayer () {
      this.$emit('closeLayer')
    },
    onConfirm () {
      this.hasTermsError = false

      if (!this.isTermsAgree) {
        this.hasTermsError = true
        return
      }
      this.$emit('onConfirm', {
        newPlanNumber: this.newPlanNumber,
        isUpgrade: this.isUpgrade,
        isDowngrade: this.isDowngrade
      })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.change-plan--wrap {
  padding: 30px 30px 25px;
  box-sizing: border-box;

  .title {
    font-size: 15px;
    font-weight: bold;
    color: #2f2f2f;
  }

  .header {
    margin-top: 38px;
    text-align: center;
    .label {
      font-size: 13px;
      color: #444;
    }
    strong {
      margin-top: 11px;
      display: block;
      font-size: 26px;
      font-weight: bold;
      color: $brandColor;
    }
    .icon {
      margin: 16px 77px 0;
    }
    .current-plan,
    .next-plan {
      display: inline-block;
    }
  }

  .plan-info--wrap {
    margin-top: 23px;
    padding: 30px;
    box-sizing: border-box;
    border-radius: 5px;
    border: solid 1px #d7d7d7;
    background-color: #f4f4f4;

    .next-plan,
    .current-plan,
    .total-price {
      @include flexXbetween;
      align-items: center;

      .label {
        font-size: 14px;
        color: #666666;
      }

      .value {
        text-align: right;
        p {
          font-size: 16px;
          font-weight: bold;
          color: #2f2f2f;
        }
        span {
          display: inline-block;
          font-size: 12px;
          color: #444444;
          margin-top: 8px;
        }
      }
    }

    .current-plan {
      border-top: 1px dashed #d7d7d7;
      margin-top: 26px;
      padding-top: 26px;
      padding-bottom: 26px;
      .value p,
      .value span {
        color: $brandColor;
      }
    }

    .total-price {
      border-top: 2px solid #c1c1c1;
      padding-top: 15px;
    }
  }

  .notice--wrap {
    margin-top: 25px;

    ul > li {
      position: relative;
      padding-left: 20px;
      font-size: 13px;
      line-height: 1.46;
      color: #666666;

      &:before {
        content: '';
        display: inline-block;
        width: 3px;
        height: 3px;
        background-color: #888;
        position: absolute;
        top: 7px;
        left: 10px;
      }

      &:not(:first-child) {
        margin-top: 10px;
      }

      ol {
        margin-top: 10px;

        li {
          @include flexXbetween;

          &:not(:first-child) {
            margin-top: 5px;
          }

          button {
            font-size: 12px;
            font-weight: bold;
            text-align: right;
            color: #555555;
          }
        }
      }
    }
  }

  .footer {
    margin-top: 25px;
    padding-top: 25px;
    border-top: 1px solid #d8d8d8;
    text-align: center;
    position: relative;

    .button--wrap {
      margin-top: 40px;

      .btn__confirm {
        margin-left: 5px;
      }
    }

    .ui_checkbox {
      margin-left: 0;
    }
    .terms-agree {
      position: relative;
      text-align: left;
    }
    .terms-agree-validation-text {
      position: absolute;
      top: 50px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 13px;
      color: $brandColor;
      white-space: nowrap;
      display: inline-block;
    }
  }
}
</style>
